import { Link } from "react-router-dom";
import { ELEMENTS, CICLO_GERACAO, CICLO_CONTROLE, CICLOS_DESCRICAO } from "@/data/elements";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag } from "@/components/common";
import ElementsCycle from "@/components/ElementsCycle";
import { ArrowRight } from "lucide-react";

export default function Elements() {
  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="A linguagem da natureza"
        title="Os Cinco Elementos"
        subtitle="Na MTC, tudo é formado pela interação de cinco grandes forças da natureza: Madeira, Fogo, Terra, Metal e Água. Não são substâncias físicas, mas qualidades energéticas que se transformam, se apoiam e se equilibram."
      />

      {/* Ilustração Oficial dos Cinco Elementos — Protagonista Visual */}
      <section
        data-testid="section-cinco-elementos-arte"
        className="relative mx-auto flex w-full max-w-5xl flex-col items-center justify-center py-1 sm:py-4"
        aria-label="Ilustração dos Cinco Elementos"
      >
        <div className="relative w-full flex justify-center items-center">
          <img
            src="/images/elements/cinco-elementos.webp"
            alt="Representação visual dos cinco elementos: Madeira, Fogo, Terra, Metal e Água"
            className="w-full max-w-4xl h-auto object-contain select-none"
            width={1536}
            height={1024}
            loading="eager"
          />
        </div>
      </section>

      {/* Cinco Elementos — Cards Interativos */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {ELEMENTS.map((e) => {
          const c = elementColor(e.id);
          return (
            <Link
              key={e.id}
              to={`/elementos/${e.id}`}
              data-testid={`element-card-${e.id}`}
              className="group overflow-hidden rounded-3xl border border-border p-7 transition-all hover:-translate-y-1 hover:border-gold/40"
              style={{ background: `linear-gradient(160deg, ${c.soft}, hsl(var(--card)))` }}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl font-semibold" style={{ color: c.text }}>{e.name}</h3>
                <SourceTag pages={e.sourcePages} />
              </div>
              <p className="mt-1 text-sm font-medium" style={{ color: c.primary }}>{e.palavras.join(" · ")}</p>
              <div className="mt-4 space-y-1 text-sm" style={{ color: c.text }}>
                <p><span className="opacity-70">Órgãos:</span> {e.orgaos.join(", ")}</p>
                <p><span className="opacity-70">Emoção:</span> {e.emocao} · <span className="opacity-70">Sabor:</span> {e.sabor}</p>
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={{ color: c.primary }}>
                Explorar <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          );
        })}
      </div>

      {/* Ciclos Wu Xing: Geração e Controle */}
      <div className="grid gap-8 lg:grid-cols-2 lg:items-center rounded-3xl border border-border bg-card p-7 sm:p-10">
        <ElementsCycle />
        <div className="space-y-5">
          <div>
            <h3 className="font-serif text-xl font-medium">Ciclo de Geração (Sheng)</h3>
            <p className="mt-1 text-sm text-muted-foreground">{CICLOS_DESCRICAO.geracao}</p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-sm">
              {CICLO_GERACAO.map((g, i) => (
                <span key={i} className="rounded-full bg-secondary px-3 py-1 text-foreground">{g.texto}</span>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-serif text-xl font-medium">Ciclo de Controle (Ke)</h3>
            <p className="mt-1 text-sm text-muted-foreground">{CICLOS_DESCRICAO.controle}</p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-sm">
              {CICLO_CONTROLE.map((g, i) => (
                <span key={i} className="rounded-full bg-gold/15 px-3 py-1 text-foreground">{g.texto}</span>
              ))}
            </div>
          </div>
          <p className="text-sm italic text-muted-foreground">Quando esses ciclos estão em harmonia, a energia (Qi) flui livremente. Quando há desequilíbrio, podem surgir sinais físicos e emocionais.</p>
        </div>
      </div>

      <SafetyNote>Os cinco elementos se transformam, se apoiam e se regulam o tempo todo. Este conteúdo é educativo e não substitui avaliação profissional.</SafetyNote>
    </div>
  );
}
